import React, { useState } from 'react';
import Modal from '../../../components/common/Modal'; // Assuming a common Modal component exists
// Removed LoadingSpinner import as it's not suitable for inline use

interface AddChatModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddChat: (phoneNumber: string) => void; // Callback to trigger adding chat
    isLoading: boolean; // To show loading state
}

const AddChatModal: React.FC<AddChatModalProps> = ({ isOpen, onClose, onAddChat, isLoading }) => {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [error, setError] = useState<string | null>(null);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Basic input cleaning (allow digits, +, spaces, parentheses)
        const cleanedValue = event.target.value.replace(/[^0-9+()\s-]/g, '');
        setPhoneNumber(cleanedValue);
        setError(null); // Clear error on input change
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        // Basic validation (e.g., check if not empty, maybe length)
        // More robust validation (like E.164 format) should happen on the backend
        // or use a library like libphonenumber-js on the frontend if needed.
        const trimmedNumber = phoneNumber.trim();
        if (!trimmedNumber) {
            setError('Номер телефона не может быть пустым.');
            return;
        }
        // Simple check for likely insufficient length (adjust as needed)
        if (trimmedNumber.replace(/\D/g, '').length < 10) {
             setError('Номер телефона слишком короткий.');
             return;
        }

        setError(null);
        onAddChat(trimmedNumber); // Pass the trimmed number to the parent handler
    };

    // Clear state when modal closes
    const handleClose = () => {
        setPhoneNumber('');
        setError(null);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Добавить чат по номеру телефона">
            <form onSubmit={handleSubmit}>
                <div className="p-4">
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-500 mb-1">
                        Номер телефона
                    </label>
                    <input
                        type="tel" // Use type="tel" for better mobile UX
                        id="phoneNumber"
                        name="phoneNumber"
                        value={phoneNumber}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="+7 7XX XXX XX XX"
                        disabled={isLoading} // Disable input while loading
                        autoFocus // Focus input when modal opens
                    />
                    {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
                </div>
                <div className="bg-gray-50 dark:bg-gray-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                        type="submit"
                        disabled={isLoading} // Disable button while loading
                        className={`w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {isLoading ? (
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : null}
                        {isLoading ? 'Добавление...' : 'Добавить'}
                    </button>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={isLoading} // Disable close button while loading
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                    >
                        Отмена
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default AddChatModal;
